import { Component } from '@theme/component';

/**
 * Stricter than the browser's built-in `type="email"` validation, which accepts
 * addresses without a dotted domain (for example `abc@gm`). Require a dotted
 * domain with a real top-level domain.
 */
const STRICT_EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

const INVALID_MESSAGE = 'Please enter a valid email address.';

/**
 * Adds inline email validation feedback to the newsletter signup form.
 *
 * The input uses `type="email"` and `required`, but that still accepts some
 * malformed addresses, so this component applies a stricter check and surfaces
 * the message next to the field instead of only in the browser bubble.
 *
 * @extends {Component}
 */
class EmailSignupComponent extends Component {
  /** @returns {HTMLInputElement | null} */
  get #input() {
    return this.querySelector('input[type="email"]');
  }

  /** @returns {HTMLElement | null} */
  get #errorElement() {
    return this.querySelector('[data-email-signup-error]');
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('invalid', this.#onInvalid, true);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('change', this.#onInput);

    const input = this.#input;
    if (input) this.#applyValidity(input);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('invalid', this.#onInvalid, true);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('change', this.#onInput);

    super.disconnectedCallback();
  }

  /** @param {Event} event */
  #onSubmit = (event) => {
    const input = this.#input;
    if (!input) return;

    this.#applyValidity(input);

    if (!input.validity.valid) {
      this.#showError(input.validationMessage);
      event.preventDefault();
      return;
    }

    this.#clearError();
  };

  /** @param {Event} event */
  #onInvalid = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    this.#showError(input.validationMessage);
  };

  #onInput = () => {
    const input = this.#input;
    if (!input) return;

    this.#applyValidity(input);
    if (input.validity.valid) this.#clearError();
  };

  /** @param {HTMLInputElement} input */
  #applyValidity(input) {
    const value = input.value.trim();

    if (value === '') {
      // Let the browser's `required` constraint report the empty field.
      input.setCustomValidity('');
    } else {
      input.setCustomValidity(STRICT_EMAIL_PATTERN.test(value) ? '' : INVALID_MESSAGE);
    }
  }

  /** @param {string} message */
  #showError(message) {
    const input = this.#input;
    const errorElement = this.#errorElement;
    if (!errorElement || !message) return;

    errorElement.textContent = message;
    errorElement.hidden = false;

    if (input) {
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', errorElement.id);
    }
  }

  #clearError() {
    const input = this.#input;
    const errorElement = this.#errorElement;
    if (!errorElement) return;

    errorElement.textContent = '';
    errorElement.hidden = true;

    if (input) {
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }
  }
}

if (!customElements.get('email-signup-component')) {
  customElements.define('email-signup-component', EmailSignupComponent);
}
